import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { LEGAL_DOCS, LEGAL_DOC_ORDER } from '@/lib/legalDocs';
import { recordLegalAcceptance } from '@/lib/legalAcceptance';
import {
  useLegalAcceptanceStatus,
  legalAcceptanceQueryKey,
} from '@/hooks/useLegalAcceptanceStatus';
import { useAuth } from '@/hooks/useAuth';

/**
 * Post-auth safety net for the legal document set.
 *
 * Consent is normally captured on the signup path (`/auth?mode=signup`) and
 * flushed once the session exists. This gate covers the remainder: accounts
 * created before click-accept existed, and future document version bumps.
 *
 * It renders nothing at all in the common case — a signed-in user with a
 * complete record passes straight through, and the status read never blocks
 * the first paint.
 */
const LegalAcceptanceGate = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data, isFetched } = useLegalAcceptanceStatus();
  const [saving, setSaving] = useState(false);

  // Unknown or accepted — never interrupt.
  if (!isFetched || data?.accepted !== false) return <>{children}</>;

  const handleAccept = async () => {
    setSaving(true);
    const ok = await recordLegalAcceptance('signup');
    setSaving(false);
    if (!ok) {
      toast.error('Could not record your acceptance', {
        description: 'Please try again in a moment.',
      });
      return;
    }
    await queryClient.invalidateQueries({ queryKey: legalAcceptanceQueryKey(user?.id) });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-lg">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2 text-primary">
            <FileText className="h-5 w-5" />
            <h1 className="text-lg font-semibold text-foreground">
              One quick step before you continue
            </h1>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            We've updated the agreements that govern your use of cannafax.ai. Please review
            and accept them to continue — you only need to do this once.
          </p>

          <ul className="mt-5 space-y-2 rounded-lg border border-border bg-muted/30 p-4">
            {LEGAL_DOC_ORDER.map((slug) => {
              const doc = LEGAL_DOCS[slug];
              return (
                <li key={slug} className="text-sm">
                  <Link
                    to={doc.path}
                    target="_blank"
                    className="font-medium underline hover:text-primary transition-colors"
                  >
                    {doc.label}
                  </Link>
                  <span className="text-muted-foreground"> — {doc.summary}</span>
                </li>
              );
            })}
          </ul>

          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            By clicking Agree and proceed, you agree, on behalf of your organization, to the
            documents above, and confirm you have authority to do so.
          </p>

          <Button
            className="mt-6 w-full"
            size="lg"
            disabled={saving}
            onClick={handleAccept}
          >
            {saving ? 'Recording…' : 'Agree and proceed'}
          </Button>

          <p className="mt-4 text-xs text-muted-foreground text-center">
            Your acceptance is recorded with the document version, date and time.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default LegalAcceptanceGate;